/**
 * Ouverture d'un PDF de vente (facture) ou de proforma dans un onglet.
 *
 * Historiquement ces documents étaient POUSSÉS en téléchargement : un clic
 * déposait un fichier dans le dossier de téléchargements sans que personne ne
 * l'ait vu. Pour relire un devis avant de l'envoyer, il fallait ouvrir le
 * fichier à la main. On aligne donc le comportement sur celui des listes
 * (cf. `components/shared/TableExport.tsx`) : le PDF s'ouvre, et c'est la
 * visionneuse du navigateur qui offre impression et téléchargement.
 *
 * Le backend sert déjà ces PDF en `Content-Disposition: inline`
 * (`apps/sales/views.py`) : rien à changer côté serveur.
 */

/** `opened` : onglet ouvert. `downloaded` : repli, le navigateur a bloqué. */
export type OpenPdfOutcome = "opened" | "downloaded";

/**
 * L'URL d'objet ne doit PAS être révoquée immédiatement : l'onglet qui vient
 * de s'ouvrir n'a pas encore chargé le blob et afficherait une page vide.
 * Même délai que l'export des listes.
 */
const REVOKE_DELAY_MS = 60_000;

export function openPdfBlob(blob: Blob, filename: string): OpenPdfOutcome {
  const url = URL.createObjectURL(blob);

  // PAS de `noopener` dans les options : la spécification impose alors un
  // retour `null`, ce qui rendrait le blocage de fenêtre indétectable et
  // ferait retomber TOUT LE MONDE sur le téléchargement. On ouvre donc sans,
  // puis on coupe `opener` à la main — même protection, retour exploitable.
  const tab = window.open(url, "_blank");
  if (tab) {
    tab.opener = null;
    window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
    return "opened";
  }

  // Fenêtre bloquée par le navigateur : mieux vaut le fichier que rien.
  return downloadInstead(url, filename);
}

/**
 * Ouvre l'adresse d'un document dans un onglet — pour ceux que l'on CONSULTE
 * plutôt que l'on fige (relevé mensuel, bordereau).
 *
 * Contrairement à `openPdfBlob`, l'onglet reçoit une vraie adresse : le
 * document y est redemandé au serveur à chaque chargement, donc le bouton
 * « actualiser » du navigateur montre les enregistrements arrivés depuis. Une
 * URL d'objet, elle, n'est qu'un instantané, et devient invalide une fois
 * révoquée. La navigation reste sur la même origine : les cookies de session
 * partent avec elle.
 *
 * Retourne `false` si le navigateur bloque les fenêtres pour ce site :
 * l'appelant le dit à l'utilisateur au lieu de le laisser sans rien.
 */
export function openDocumentTab(url: string): boolean {
  const tab = window.open(url, "_blank");
  if (!tab) return false;
  // Même protection que `noopener`, sans en perdre la valeur de retour.
  tab.opener = null;
  return true;
}

/** Dépose le fichier : dernier recours quand aucune fenêtre ne s'ouvre. */
function downloadInstead(url: string, filename: string): OpenPdfOutcome {
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  return "downloaded";
}
